import {
  getRadStakeContract,
  getStakePatchContract,
  getReadWeb3,
  getAccountAddress,
} from "../index";
import { ZERO_ADDRESS, getCardLevelByTokenId } from "../constants";
// import { DEFAULT_PARENT_ADDRESS } from "@/views/pledge/config";
const DEFAULT_PARENT_ADDRESS = '0x1d5de569B1c80597f6503F3970bD1d35Ed5Ab1E4'
import {
  CARD_USER_PROFILE,
  calcCardBenefitPercent,
  calcCardBenefitRange,
} from "@/views/pledge/config";
import { ensureRadAllowanceForRadStake } from "./tokenService";
import {
  assertWalletReady,
  formatTokenAmount,
  parseTokenAmount,
  sendContractMethod,
} from "./txHelper";

function logClaimTransactionHash(label, receipt) {
  const hash = receipt?.transactionHash;
  console.log(`[${label}] transactionHash:`, hash);
  return hash;
}

/** Web3 多返回值常为 { 0: v0, 1: v1 } 对象，转为数组以便解构 */
function asContractTuple(result, length) {
  if (Array.isArray(result)) {
    return result;
  }
  if (result == null || typeof result !== "object") {
    return length === 1 ? [result] : [];
  }
  return Array.from({ length }, (_, index) => result[index]);
}

function countUserCards(cardIds) {
  let goldCards = 0;
  let silverCards = 0;
  let bronzeCards = 0;

  cardIds.forEach((tokenId) => {
    const level = getCardLevelByTokenId(tokenId);
    if (level === "gold") goldCards += 1;
    if (level === "silver") silverCards += 1;
    if (level === "bronze") bronzeCards += 1;
  });

  return { goldCards, silverCards, bronzeCards };
}

function countActivatedUserCards(cardIds, isActive) {
  const activatedIds = cardIds.filter((_, index) => Boolean(isActive[index]));
  return countUserCards(activatedIds);
}

function getCardImageByLevel(level) {
  if (level === "gold") return CARD_USER_PROFILE.gold.avatarSrc;
  if (level === "silver") return CARD_USER_PROFILE.silver.avatarSrc;
  if (level === "bronze") return CARD_USER_PROFILE.bronze.avatarSrc;
  return "";
}

function resolveCardUserProfile(counts) {
  const { goldCards, silverCards, bronzeCards } = counts;
  if (goldCards + silverCards + bronzeCards === 0) {
    return null;
  }
  const bonusRange = calcCardBenefitRange(goldCards, silverCards, bronzeCards);
  if (goldCards > 0) {
    return { level: "gold", ...CARD_USER_PROFILE.gold, bonusRange, ...counts };
  }
  if (silverCards > 0) {
    return { level: "silver", ...CARD_USER_PROFILE.silver, bonusRange, ...counts };
  }
  return { level: "bronze", ...CARD_USER_PROFILE.bronze, bonusRange, ...counts };
}

async function fetchRadUserCardsRaw(userAddress) {
  const radStake = getRadStakeContract();
  const result = await radStake.methods
    .getUserCardsByOldStake(userAddress)
    .call();
  const [cardIds, isActive] = asContractTuple(result, 2);
  return { cardIds: cardIds || [], isActive: isActive || [] };
}

export async function fetchRadUserCards(userAddress) {
  const { cardIds, isActive } = await fetchRadUserCardsRaw(userAddress);
  return cardIds.map((tokenId, index) => {
    const level = getCardLevelByTokenId(tokenId);
    return {
      tokenId: String(tokenId),
      activated: Boolean(isActive[index]),
      level,
      image: getCardImageByLevel(level),
    };
  });
}

export async function fetchRadUserCardProfile(userAddress) {
  if (!userAddress) {
    return null;
  }
  const cards = await fetchRadUserCards(userAddress);
  if (cards.length === 0) {
    return null;
  }

  const activatedTokenIds = cards
    .filter((item) => item.activated)
    .map((item) => item.tokenId);
  const activatedCounts = countUserCards(activatedTokenIds);
  const bonusRange = calcCardBenefitRange(
    activatedCounts.goldCards,
    activatedCounts.silverCards,
    activatedCounts.bronzeCards
  );

  const hasActivated =
    activatedCounts.goldCards +
      activatedCounts.silverCards +
      activatedCounts.bronzeCards >
    0;
  const displayCounts = hasActivated
    ? activatedCounts
    : countUserCards(cards.map((item) => item.tokenId));
  const profile = resolveCardUserProfile(displayCounts);
  if (!profile) {
    return null;
  }

  return {
    ...profile,
    bonusRange,
    cards,
    hasUnactivated: cards.some((item) => !item.activated),
  };
}

function calcRemainingDays(startTime, lockDays, daySeconds, now) {
  const maturityTime = Number(startTime) + Number(lockDays) * daySeconds;
  const remainingSeconds = maturityTime - now;
  if (remainingSeconds <= 0) {
    return 0;
  }
  return Math.ceil(remainingSeconds / daySeconds);
}

function formatPeriodQuota(result) {
  const [staked, total, remaining] = asContractTuple(result, 3);
  const stakedBn = BigInt(staked);
  const totalBn = BigInt(total);
  let progress = 0;
  if (totalBn > 0n) {
    progress = Number((stakedBn * 10000n) / totalBn) / 100;
  }
  return {
    staked: formatTokenAmount(staked),
    total: formatTokenAmount(total),
    remaining: formatTokenAmount(remaining),
    progress: Math.min(100, Math.round(progress * 100) / 100),
  };
}

async function fetchRadStakeParams() {
  const radStake = getRadStakeContract();
  const [
    daySeconds,
    maxClaimStakeAmount,
    rewardPerSecond180,
    rewardPerSecond360,
    rewardPerSecond720,
  ] = await Promise.all([
    radStake.methods.daySeconds().call(),
    radStake.methods.maxClaimStakeAmount().call(),
    radStake.methods.rewardPerSecond180().call(),
    radStake.methods.rewardPerSecond360().call(),
    radStake.methods.rewardPerSecond720().call(),
  ]);
  return {
    daySeconds: Number(daySeconds),
    maxClaimStakeAmount: BigInt(maxClaimStakeAmount),
    rewardPerSecond: {
      180: BigInt(rewardPerSecond180),
      360: BigInt(rewardPerSecond360),
      720: BigInt(rewardPerSecond720),
    },
  };
}

function calcPendingBoxReward(stakeInfo, params, now) {
  let currentTime = now;
  const endTime = Number(stakeInfo.endTime);
  const lastClaimTime = Number(stakeInfo.lastClaimTime);
  if (currentTime > endTime) {
    currentTime = endTime;
  }

  let newReward = 0n;
  if (currentTime > lastClaimTime) {
    const period = Number(stakeInfo.period);
    const rewardPerSecond = params.rewardPerSecond[period] || 0n;
    const secondsPassed = BigInt(currentTime - lastClaimTime);
    const stakeAmount = BigInt(stakeInfo.stakeAmount);
    newReward =
      (stakeAmount * rewardPerSecond * secondsPassed) / 10n ** 18n;
  }
  console.log(newReward,'newReward')
  return newReward + BigInt(stakeInfo.pendingReward || 0);
}

function canClaimStakeReward(stakeInfo, params, now) {
  let currentTime = now;
  const endTime = Number(stakeInfo.endTime);
  const lastClaimTime = Number(stakeInfo.lastClaimTime);
  if (currentTime > endTime) {
    currentTime = endTime;
  }
  if (currentTime <= lastClaimTime) {
    return false;
  }
  return calcPendingBoxReward(stakeInfo, params, now) > 0n;
}

export function getRadStakeParentFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const parent = params.get("parent");
  if (!parent || !getReadWeb3().utils.isAddress(parent)) {
    return "";
  }
  return getReadWeb3().utils.toChecksumAddress(parent);
}

export function resolveRadStakeParentAddress(boundParent) {
  if (boundParent && boundParent !== ZERO_ADDRESS) {
    return boundParent;
  }
  return getRadStakeParentFromUrl() || DEFAULT_PARENT_ADDRESS;
}

export async function getMinStakeAmount() {
  const radStake = getRadStakeContract();
  const min = await radStake.methods.minStakeAmount().call();
  return formatTokenAmount(min);
}

export async function getRadStakeParentAddress(userAddress) {
  const radStake = getRadStakeContract();
  const parent = await radStake.methods.getParent(userAddress).call();
  if (!parent || parent === ZERO_ADDRESS) {
    return "";
  }
  return parent;
}

export async function fetchPeriodQuotas() {
  const radStake = getRadStakeContract();
  const [reward180, reward360, reward720] = await Promise.all([
    radStake.methods.getReward180().call(),
    radStake.methods.getReward360().call(),
    radStake.methods.getReward720().call(),
  ]);
  return {
    180: formatPeriodQuota(reward180),
    360: formatPeriodQuota(reward360),
    720: formatPeriodQuota(reward720),
  };
}

export async function fetchRadStakingRecords(userAddress) {
  const radStake = getRadStakeContract();
  const params = await fetchRadStakeParams();
  const stakes = await radStake.methods.getUserStakes(userAddress).call();
  console.log(stakes,'stakes')
  const now = Math.floor(Date.now() / 1000);
  const records = stakes
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => !item.isPrincipalClaimed)
    .map(({ item, index }) => {
      const period = Number(item.period);
      const remainingDays = calcRemainingDays(
        item.startTime,
        period,
        params.daySeconds,
        now
      );
      const pendingBox = calcPendingBoxReward(item, params, now);
      console.log(pendingBox,'pendingBox')
      return {
        id: index + 1,
        index,
        remainingTime: `${remainingDays}天`,
        remainingDays,
        type: `${period}天`,
        amount: formatTokenAmount(item.stakeAmount),
        reward: formatTokenAmount(String(pendingBox)),
        canRedeem: remainingDays === 0,
        hasPendingReward: canClaimStakeReward(item, params, now),
      };
    });
    console.log(records,'records')
  return records;
}

export async function fetchRadTeamInfo(userAddress) {
  const radStake = getRadStakeContract();
  const stakePatch = getStakePatchContract();
  const [userInfo, claimedReward, cardsRaw, claimQuota] = await Promise.all([
    radStake.methods.getUserInfo(userAddress).call(),
    radStake.methods.getClaimedReward(userAddress).call(),
    fetchRadUserCardsRaw(userAddress),
    stakePatch.methods.getClaimQuota(userAddress).call(),
  ]);
  console.log(claimQuota,'claimQuota')
  const { cardIds, isActive } = cardsRaw;
  const { goldCards, silverCards, bronzeCards } = countActivatedUserCards(
    cardIds,
    isActive
  );
  const totalCards = goldCards + silverCards + bronzeCards;
  const userInfoTuple = asContractTuple(userInfo, 5);
  const claimedTuple = asContractTuple(claimedReward, 3);

  return {
    depth: Number(userInfoTuple[1]),
    directPerformance: `${formatTokenAmount(userInfoTuple[2])} RAD`,
    totalPerformance: `${formatTokenAmount(userInfoTuple[3])} RAD`,
    promotionReward: `${formatTokenAmount(userInfoTuple[4])} BOX`,
    totalCards,
    cardBenefit: calcCardBenefitPercent(goldCards, silverCards, bronzeCards),
    cardReward: `${formatTokenAmount(userInfoTuple[0])} BOX`,
    claimQuota: formatTokenAmount(claimQuota),
    withdrawnStaking: `${formatTokenAmount(claimedTuple[0])} BOX`,
    withdrawnPromotion: `${formatTokenAmount(claimedTuple[1])} BOX`,
    withdrawnCard: `${formatTokenAmount(claimedTuple[2])} BOX`,
    pendingPromotionReward: BigInt(userInfoTuple[4]),
    pendingCardReward: BigInt(userInfoTuple[0]),
  };
}

export async function stakeRadToken(amount, period, parentAddress) {
  assertWalletReady();
  const amountWei = parseTokenAmount(amount);
  await ensureRadAllowanceForRadStake(amountWei);

  const radStake = getRadStakeContract();
  const parent = parentAddress || ZERO_ADDRESS;
  const method = radStake.methods.stakeToken(amountWei, parent, period);
  return sendContractMethod(method);
}

export async function claimRadPrincipal(index) {
  assertWalletReady();
  const radStake = getRadStakeContract();
  const method = radStake.methods.claimPrincipal(index);
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("赎回本金", receipt);
  return receipt;
}

export async function claimRadStakeReward(index) {
  assertWalletReady();
  const radStake = getRadStakeContract();
  const method = radStake.methods.claimStakeReward(index);
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("领取质押收益", receipt);
  return receipt;
}

export async function claimAllRadStakeRewards(userAddress) {
  assertWalletReady();
  const records = await fetchRadStakingRecords(userAddress);
  const pendingRecords = records.filter((item) => item.hasPendingReward);
  if (pendingRecords.length === 0) {
    throw new Error("暂无可提取的质押收益");
  }

  const radStake = getRadStakeContract();
  for (const record of pendingRecords) {
    const method = radStake.methods.claimStakeReward(record.index);
    const receipt = await sendContractMethod(method);
    logClaimTransactionHash("提取质押收益", receipt);
  }
}

export async function claimRadShareReward() {
  assertWalletReady();
  const radStake = getRadStakeContract();
  const method = radStake.methods.claimShareReward();
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("提取推广收益", receipt);
  return receipt;
}

export async function claimRadCardsReward() {
  assertWalletReady();
  const radStake = getRadStakeContract();
  const method = radStake.methods.claimCardsReward();
  const receipt = await sendContractMethod(method);
  logClaimTransactionHash("提取卡牌收益", receipt);
  return receipt;
}

export async function activateAllCards() {
  assertWalletReady();
  const userAddress = getAccountAddress();
  const radStake = getRadStakeContract();
  const { cardIds, isActive } = await fetchRadUserCardsRaw(userAddress);
  if (!cardIds || cardIds.length === 0) {
    throw new Error("暂无可激活的卡牌");
  }

  const toActivate = cardIds.filter((_, index) => !isActive[index]);
  if (toActivate.length === 0) {
    throw new Error("卡牌已全部激活");
  }

  const method = radStake.methods.activateCardBatch(toActivate);
  await sendContractMethod(method);
}

export async function fetchUnactivatedCardCount(userAddress) {
  const { cardIds, isActive } = await fetchRadUserCardsRaw(userAddress);
  if (!cardIds || cardIds.length === 0) {
    return 0;
  }
  return isActive.filter((activated) => !activated).length;
}
